import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip as MuiTooltip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useUserStore } from '../../stores/userStore'
import { useSurveyStore } from '../../stores/surveyStore'
import {
  SURVEY_TYPE_LABELS,
  SURVEY_DIMENSION_LABELS,
  SURVEY_MAX_SCORES,
} from '../../models/Survey'
import { getScreenLabel } from '../../utils/screenNames'
import { exportUserReport } from '../../utils/excelExport'
import { formatDuration } from '../../utils/formatDuration'
import { useCareerCategoryStore } from '../../stores/careerCategoryStore'
import { calculateCareerCategory } from '../../utils/careerCategoryEngine'
// ScreenLog and UsageStats types are used via the store

const COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#607D8B', '#795548', '#3F51B5',
]

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const {
    selectedUser,
    screenLogs,
    usageStats,
    loading,
    error,
    fetchUser,
    fetchUserScreenLogs,
    fetchUserUsageStats,
    clearSelectedUser,
  } = useUserStore()

  const { userSurveys, fetchUserSurveys, clearUserSurveys } = useSurveyStore()
  const { categories, fetchCategories } = useCareerCategoryStore()

  const [logsLimit, setLogsLimit] = useState(20)

  useEffect(() => {
    if (userId) {
      fetchUser(userId)
      fetchUserScreenLogs(userId, 50)
      fetchUserUsageStats(userId)
      fetchUserSurveys(userId)
    }
    return () => {
      clearSelectedUser()
      clearUserSurveys()
    }
  }, [userId])

  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories()
    }
  }, [categories.length, fetchCategories])

  if (loading && !selectedUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!selectedUser) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="warning">找不到此用戶</Alert>
      </Box>
    )
  }

  const user = selectedUser

  // 取每種量表的最新一份（completedAt 最大）
  const latestSurveysByType: {
    career_calling?: typeof userSurveys[number]
    outcome_expectation?: typeof userSurveys[number]
    bfi15?: typeof userSurveys[number]
  } = {}
  for (const s of userSurveys) {
    const current = latestSurveysByType[s.surveyType]
    if (!current || s.completedAt > current.completedAt) {
      latestSurveysByType[s.surveyType] = s
    }
  }

  const careerResult = calculateCareerCategory(latestSurveysByType, categories)
  const completedSurveyTypes = Object.values(latestSurveysByType).filter(Boolean).length
  const allSurveysCompleted = completedSurveyTypes === 3

  // 組裝圖表資料
  const chartData = usageStats?.screenTimeByPage
    ? Object.entries(usageStats.screenTimeByPage)
        .map(([name, seconds]) => ({
          name: getScreenLabel(name),
          seconds,
          label: formatDuration(seconds),
        }))
        .sort((a, b) => b.seconds - a.seconds)
    : []

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/users')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            用戶詳情
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => exportUserReport(user, screenLogs, usageStats, userSurveys)}
        >
          匯出報告
        </Button>
      </Box>

      {/* 基本資訊 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            基本資訊
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                姓名
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {user.displayName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{user.email || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                性別
              </Typography>
              <Typography variant="body1">
                {{ male: '男', female: '女', other: '其他' }[user.gender] ||
                  user.gender}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                學歷 / 年級
              </Typography>
              <Typography variant="body1">
                {{ bachelor: '學士', master: '碩士', phd: '博士' }[
                  user.education
                ] || user.education}{' '}
                {user.grade} 年級
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                註冊時間
              </Typography>
              <Typography variant="body1">
                {format(user.registeredAt, 'yyyy/MM/dd HH:mm', {
                  locale: zhTW,
                })}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                註冊方式
              </Typography>
              <Chip
                label={user.registeredBy === 'qr_code' ? 'QR Code' : 'Email'}
                size="small"
                color={user.registeredBy === 'qr_code' ? 'primary' : 'default'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 使用統計摘要 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  總使用時間
                </Typography>
                <MuiTooltip title="App 啟動到背景/結束之間累計的前景時間（users.usageStats.totalScreenTime）。和「各頁面瀏覽時間總和」會有差異：頁面總和只計實際進入過的頁面，且 splash/login/main 不計。">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </MuiTooltip>
              </Stack>
              <Typography variant="h5" fontWeight={600} color="primary">
                {formatDuration(user.totalScreenTime)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  登入次數
                </Typography>
                <MuiTooltip title="包含 Email 登入與 QR Code 登入；每次成功登入 +1（loginCount）。">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </MuiTooltip>
              </Stack>
              <Typography variant="h5" fontWeight={600} color="primary">
                {user.loginCount} 次
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Session 數
                </Typography>
                <MuiTooltip title="使用者連續使用一段時間算一次 Session；閒置超過設定時間（預設 10 分鐘）即結束。可在「數據分析 → Session 設定」調整。">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </MuiTooltip>
              </Stack>
              <Typography variant="h5" fontWeight={600} color="primary">
                {usageStats?.totalSessions ?? 0} 次
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                最後上線
              </Typography>
              <Typography variant="h6" fontWeight={500}>
                {user.lastSeen
                  ? format(user.lastSeen, 'MM/dd HH:mm', { locale: zhTW })
                  : user.lastLoginAt
                    ? format(user.lastLoginAt, 'MM/dd HH:mm', { locale: zhTW })
                    : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 各頁面停留時間圖表 */}
      {chartData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              各頁面停留時間
            </Typography>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => formatDuration(v)}
                  />
                  <YAxis type="category" dataKey="name" width={80} />
                  <Tooltip
                    formatter={(value: number) => [
                      formatDuration(value),
                      '停留時間',
                    ]}
                  />
                  <Bar dataKey="seconds" radius={[0, 4, 4, 0]}>
                    {chartData.map((_entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 最近的 Screen Logs */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            最近使用紀錄
          </Typography>
          {screenLogs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              尚無使用紀錄
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>頁面</TableCell>
                      <TableCell>停留時間</TableCell>
                      <TableCell>進入時間</TableCell>
                      <TableCell>離開時間</TableCell>
                      <TableCell>Session ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {screenLogs.slice(0, logsLimit).map((log, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={getScreenLabel(log.screenName)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDuration(log.duration)}</TableCell>
                        <TableCell>
                          {format(log.enteredAt, 'MM/dd HH:mm:ss', {
                            locale: zhTW,
                          })}
                        </TableCell>
                        <TableCell>
                          {format(log.exitedAt, 'MM/dd HH:mm:ss', {
                            locale: zhTW,
                          })}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {log.sessionId.substring(0, 8)}…
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {screenLogs.length > logsLimit && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="text"
                    onClick={() => setLogsLimit((prev) => prev + 20)}
                  >
                    載入更多
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 量表結果 */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            量表結果
          </Typography>
          {(['career_calling', 'outcome_expectation', 'bfi15'] as const).map((type) => {
            const survey = userSurveys.find((s) => s.surveyType === type)
            const dimLabels = SURVEY_DIMENSION_LABELS[type]
            const maxScore = SURVEY_MAX_SCORES[type]

            return (
              <Accordion key={type} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography fontWeight={500}>{SURVEY_TYPE_LABELS[type]}</Typography>
                    {survey ? (
                      <Chip
                        label={format(survey.completedAt, 'yyyy/MM/dd', { locale: zhTW })}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="尚未完成" size="small" sx={{ color: 'text.disabled', borderColor: 'text.disabled' }} variant="outlined" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {survey ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        主要特質：
                        <Typography component="span" fontWeight={600} color="text.primary">
                          {dimLabels[survey.dominantDimension] ?? survey.dominantDimension}
                        </Typography>
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(survey.scores).map(([key, score]) => {
                          const pct = Math.min((score / maxScore) * 100, 100)
                          return (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                              <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {dimLabels[key] ?? key}
                                </Typography>
                                <Typography variant="caption" fontWeight={600}>
                                  {score.toFixed(1)} / {maxScore}
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      此用戶尚未完成此量表。
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </CardContent>
      </Card>

      {/* 護理職涯分類 */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            護理職涯分類
          </Typography>
          {!allSurveysCompleted ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: 'text.disabled',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                完成全部 3 份量表後，解鎖護理職涯分類（目前 {completedSurveyTypes}/3）
              </Typography>
            </Box>
          ) : !careerResult ? (
            <Typography variant="body2" color="text.secondary">
              無法計算職涯分類。
            </Typography>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`#${careerResult.matchedNumbers.join(', #')}`}
                  color="primary"
                  size="small"
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  {careerResult.displayCategory.categoryLabel}
                </Typography>
                {careerResult.hasMultiple && (
                  <Chip label="同分多重命中" size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                {careerResult.displayCategory.description}
              </Typography>
              {careerResult.displayNursingJobs.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    推薦護理工作
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {careerResult.displayNursingJobs.map((job) => (
                      <Chip key={job} label={job} size="small" color="secondary" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
